// Pure evaluation of a GUI (web-dev) session's checks - the logic that used to live inline
// in TerminalContext.runTestsWebDev. Extracted so the instructor's live student preview in
// the creation wizard can grade a student's files exactly the way /workspace/:id does,
// without TerminalContext's React state, telemetry or run persistence.
//
// evaluateWebDevChecks(guiConfig, userCodeJson) -> {
//   logs:      [{ type, content }]  terminal lines, in order ('stdout' | 'stderr' | 'test_result')
//   results:   boolean[]            per-test pass/fail. A run that cannot be graded at all
//                                   (corrupted JSON, an unexpected exception) fails every
//                                   configured test rather than leaving them "not run"; []
//                                   only when the session has no tests
//   telemetry: { event: 'RESULT' | 'ERROR', payload } | null
//                                   the TERMINAL_EVENTS key and payload the student
//                                   workspace logs for this run, or null when it logs none
// }
//
// Branch order (unchanged from runTestsWebDev): corrupted JSON -> instructor custom checks
// -> hardcoded html / css / javascript phase checks -> syntax-check fallback. Every log
// line and telemetry payload is byte-for-byte what runTestsWebDev produced, and so is every
// result array except the two ungradable cases above.
// Needs a global DOMParser (any browser).

const passFailLogs = (checks) => checks.map((check) => ({
  type: check.passed ? 'test_result' : 'stderr',
  content: `${check.passed ? 'PASS' : 'NOT YET'}: ${check.name}`,
}))

// One failure per test the student sees (guiConfig.tests lines up with every branch below).
const allFailed = (guiConfig) =>
  Array.isArray(guiConfig?.tests) ? guiConfig.tests.map(() => false) : []

const checksResult = (logs, checks) => {
  const results = checks.map((check) => check.passed)
  const passedCount = results.filter(Boolean).length
  return {
    logs: [...logs, ...passFailLogs(checks)],
    results,
    telemetry: {
      event: 'RESULT',
      payload: {
        passrate: Math.round((passedCount / checks.length) * 100),
        passed_count: passedCount,
        total_tests: checks.length,
        detailed_results: checks.map((check, index) => ({
          test_case_number: index + 1,
          passed: check.passed,
          test_name: check.name,
        })),
      },
    },
  }
}

export const evaluateWebDevChecks = (guiConfig, userCodeJson) => {
  const logs = []
  const addLog = (type, content) => logs.push({ type, content })

  try {
    addLog('stdout', 'Analyzing Web Development files...')

    let parsed = { html: "", css: "", js: "" }
    try {
      parsed = JSON.parse(userCodeJson)
    } catch (e) {
      addLog('stderr', 'Error: Code workspace is corrupted. Click "Reset Template" to restore.')
      return { logs, results: allFailed(guiConfig), telemetry: null }
    }

    let errors = 0

    // 1. Validate HTML syntax
    addLog('stdout', 'Checking HTML structure...')
    const parser = new DOMParser()
    const htmlDoc = parser.parseFromString(parsed.html, "text/html")
    const parserError = htmlDoc.querySelector("parsererror")
    if (parserError) {
      errors++
      addLog('stderr', `HTML Syntax Error: ${parserError.textContent}`)
    } else {
      addLog('stdout', 'HTML structure looks clean! ✓')
    }

    // Instructor-authored custom tests (see GuiExerciseEditor) take priority over every
    // hardcoded phase branch below, regardless of phase - each check is a JS expression
    // string evaluated with htmlDoc/css/js in scope, mirroring how DEFAULT_CHECKS in
    // guiSessionConfig.js models the hardcoded branches this replaces.
    if (guiConfig?.checks?.length) {
      const cssSource = parsed.css || ''
      const jsSource = parsed.js || ''
      const checks = guiConfig.checks.map((def) => {
        let passed = false
        try {
          // eslint-disable-next-line no-new-func
          passed = Boolean(new Function('htmlDoc', 'css', 'js', `return (${def.check})`)(htmlDoc, cssSource, jsSource))
        } catch (e) {
          passed = false
        }
        return { name: def.name, passed }
      })
      return checksResult(logs, checks)
    }

    if (guiConfig?.phase === 'html') {
      const main = htmlDoc.querySelector('main')
      const image = htmlDoc.querySelector('img')
      const list = htmlDoc.querySelector('ul, ol')
      const link = htmlDoc.querySelector('a[href]')
      const checks = [
        { name: 'A main element contains an article', passed: Boolean(main?.querySelector('article')) },
        { name: 'The page has a non-empty h1 heading', passed: Boolean(htmlDoc.querySelector('h1')?.textContent.trim()) },
        { name: 'The image has meaningful alternative text', passed: Boolean(image?.getAttribute('alt')?.trim()) },
        { name: 'The profile includes an introduction paragraph', passed: Boolean(htmlDoc.querySelector('p')?.textContent.trim()) },
        { name: 'The interests list contains at least three items', passed: Boolean(list && list.querySelectorAll('li').length >= 3) },
        { name: 'The page includes a link with a destination', passed: Boolean(link?.getAttribute('href')?.trim()) },
      ]
      return checksResult(logs, checks)
    }

    if (guiConfig?.phase === 'css') {
      const css = parsed.css || ''
      const profileRule = css.match(/\.profile-card\s*\{([^}]*)\}/i)?.[1] || ''
      const imageRule = css.match(/(?:img|\.profile-card\s+img)\s*\{([^}]*)\}/i)?.[1] || ''
      const linkRule = css.match(/(?:a|\.profile-card\s+a)\s*\{([^}]*)\}/i)?.[1] || ''
      const checks = [
        { name: 'Profile card selector', passed: Boolean(profileRule) },
        { name: 'Maximum card width', passed: /max-width\s*:/i.test(profileRule) },
        { name: 'Card padding', passed: /padding(?:-[a-z]+)?\s*:/i.test(profileRule) },
        { name: 'Visible card border', passed: /border(?:-[a-z]+)?\s*:/i.test(profileRule) },
        { name: 'Rounded profile image', passed: /border-radius\s*:/i.test(imageRule) },
        { name: 'Colored link', passed: /color\s*:/i.test(linkRule) },
      ]
      return checksResult(logs, checks)
    }

    if (guiConfig?.phase === 'javascript') {
      const js = parsed.js || ''
      const checks = [
        { name: 'Selects the toggle button', passed: /(?:getElementById|querySelector)\s*\([^)]*toggle-interests/i.test(js) },
        { name: 'Selects the interests list', passed: /(?:getElementById|querySelector)\s*\([^)]*interests/i.test(js) },
        { name: 'Registers a click listener', passed: /addEventListener\s*\(\s*['"]click['"]/i.test(js) },
        { name: 'Changes the interests visibility', passed: /(?:hidden|display|classList)/i.test(js) },
      ]
      return checksResult(logs, checks)
    }

    // 2. Validate CSS syntax
    addLog('stdout', 'Checking CSS syntax...')
    const openBraces = (parsed.css.match(/{/g) || []).length
    const closeBraces = (parsed.css.match(/}/g) || []).length
    if (openBraces !== closeBraces) {
      errors++
      addLog('stderr', `CSS Syntax Warning: Mismatched curly braces. Found ${openBraces} opening '{' and ${closeBraces} closing '}'.`)
    } else {
      addLog('stdout', 'CSS syntax check passed! ✓')
    }

    // 3. Validate JS syntax
    addLog('stdout', 'Checking JavaScript compilation...')
    try {
      // eslint-disable-next-line no-new-func
      new Function(parsed.js)
      addLog('stdout', 'JavaScript compilation passed! ✓')
    } catch (e) {
      if (e instanceof SyntaxError) {
        errors++
        addLog('stderr', `JavaScript Syntax Error: ${e.message}`)
      } else {
        addLog('stdout', 'JavaScript compilation passed! ✓')
      }
    }

    if (errors === 0) {
      addLog('test_result', 'All syntax checks passed successfully! 100% completed.')
      return {
        logs,
        results: [true],
        telemetry: {
          event: 'RESULT',
          payload: {
            passrate: 100,
            passed_count: 1,
            total_tests: 1,
            detailed_results: [{ test_case_number: 1, passed: true, test_name: 'Syntax Checks' }]
          },
        },
      }
    }

    addLog('stderr', `${errors} compilation/syntax issues found. Please correct them.`)
    return {
      logs,
      results: [false],
      telemetry: {
        event: 'ERROR',
        payload: {
          message: `${errors} syntax issues found`,
          type: 'WebDevCompilationError'
        },
      },
    }
  } catch (err) {
    addLog('stderr', `Analysis failed: ${err.message}`)
    return { logs, results: allFailed(guiConfig), telemetry: null }
  }
}
